import * as Sentry from "@sentry/react";

/** Centralised breadcrumb reporting for bulk destructive ops.
 *
 *  Why this exists: when 32 CTMOBI campaigns silently paused on
 *  2026-05-04, no alert fired and no Sentry event was raised. We had
 *  no out-of-band signal that something destructive happened at
 *  scale. Now every bulk Pause/Enable lays down a Sentry breadcrumb.
 *
 *  Breadcrumbs don't surface in the dashboard on their own — they
 *  surface in the trail of any subsequent error, which is the
 *  realistic recovery path: if 32 campaigns paused and then something
 *  later 5xx'd, the Sentry issue will carry the full timeline back to
 *  the bulk action that preceded it. The backend audit_log is the
 *  standalone record of the action itself.
 *
 *  Until 2026-09-07 large fanouts (>= LOUD_THRESHOLD) ALSO raised a
 *  warning-level message event. Owner decision that day: those events
 *  were the owner's own bulk pauses showing up as "issues" and made the
 *  Sentry list unreadable next to real errors. Breadcrumb only now. */

// Breadcrumb level flips to `warning` at this many resources in a
// single bulk action, so a large fanout stands out in an error's trail.
const LOUD_THRESHOLD = 10;

export type BulkActionContext = {
  /** "campaign" / "ad_group" / "keyword" — what was affected. */
  resource: "campaign" | "ad_group" | "keyword";
  /** "pause" / "enable" / "edit_bid" / "edit_budget" / "apply_cpp" — the verb. */
  action: string;
  /** How many resources were affected by this single user action. */
  count: number;
  /** Anything else useful for forensics: target status, value, etc. */
  details?: Record<string, unknown>;
};

export function reportBulkAction(ctx: BulkActionContext) {
  const message = `bulk ${ctx.action} on ${ctx.count} ${ctx.resource}${ctx.count === 1 ? "" : "s"}`;
  Sentry.addBreadcrumb({
    category: "bulk",
    type: "user",
    level: ctx.count >= LOUD_THRESHOLD ? "warning" : "info",
    message,
    data: {
      resource: ctx.resource,
      action: ctx.action,
      count: ctx.count,
      ...(ctx.details ?? {}),
    },
  });
}
